// GrowthBook singleton instance shared across the app.
// Instantiated once at module load; attributes and features are kept in sync
// by GrowthBookProvider.
import { GrowthBook } from "@growthbook/growthbook-react";

const apiHost = process.env.GROWTHBOOK_API_HOST || "https://cdn.growthbook.io";
const clientKey = process.env.GROWTHBOOK_CLIENT_KEY || "";
const decryptionKey = process.env.GROWTHBOOK_DECRYPTION_KEY || undefined;

// Every ingested event below is sent with `attributes: { id, user_id: id }`. The managed
// warehouse fills its `user_id` column only from an explicit `user_id` attribute: `id`
// stays inside the attributes JSON and resolves to the `device_id` identifier at analysis
// time, so an event sent with `id` alone is invisible to a `user_id` aggregation unit or
// funnel. `user_id` mirrors `id`, whichever id space `id` is in.

export const gb = new GrowthBook({
  apiHost,
  clientKey,
  decryptionKey,
  enableDevMode: process.env.NODE_ENV === "development",
  trackingCallback: (experiment, result) => {
    const id = gb.getAttributes().id;

    fetch(`https://us1.gb-ingest.com/track?client_key=${clientKey}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify([
        {
          event_name: "Experiment Viewed",
          properties: {
            experimentId: experiment.key,
            variationId: result.key,
          },
          attributes: { id, user_id: id },
        },
      ]),
    }).catch(() => undefined);
  },
});

export const trackEvent = (
  eventName: string,
  properties?: Record<string, unknown>,
  visitorId?: string
) => {
  const id = visitorId || gb.getAttributes().id;

  return fetch(`https://us1.gb-ingest.com/track?client_key=${clientKey}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    // Survives the page unload when a redirect follows the event.
    keepalive: true,
    body: JSON.stringify([
      {
        event_name: eventName,
        properties: properties ?? {},
        attributes: { id, user_id: id },
      },
    ]),
  }).catch(() => undefined);
};

// Anonymous visitor id minted by the backend (see GrowthbookConcern#growthbook_tracking_id),
// rendered into the page as a meta tag so logged-out users get the same GrowthBook
// identity server-side and client-side.
export const getBackendVisitorId = (): string =>
  document
    .querySelector('meta[name="growthbook-visitor-id"]')
    ?.getAttribute("content") || "";
